import { Checkbox, DatePicker, Form, FormInstance, Input, Radio, Select } from "antd";

interface DynamicFieldProps {
  field: any;
  form: FormInstance;
  styles: any;
  setSearchValue: (val: string) => void;
}

function getPrivacyProps(field: { id?: unknown; label?: unknown }): {
  "data-dd-privacy"?: "mask";
} {
  const identity = `${field.id ?? ""} ${field.label ?? ""}`
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim();
  if (/\b(?:pep\s+)?employer\b|\bsource\s+of\s+wealth\b/.test(identity)) {
    return {};
  }
  const piiFieldPattern =
    /\b(?:email|phone|mobile|contact\s+(?:number|no)|address|postal(?:\s+code)?|zip(?:\s+code)?|identity|identification|id\s+(?:type|number|no)|nationality|ethnicity|race|date\s+of\s+birth|birthdate|dob|sex|gender|occupation|politically\s+exposed\s+person|pep(?:\s+status)?|tin|mykad|nric|passport|fingerprint|signature|password|username|login\s+id|token|otp)\b/;
  const personNamePattern =
    /\b(?:customer|person|borrower|pawner|individual|pep)(?:\s+(?:full|first|middle|last))?\s+name\b|\b(?:full|first|middle|last)\s+name\b|\bname\s+of\s+(?:customer|person|borrower|pawner|pep)\b/;

  return piiFieldPattern.test(identity) ||
    personNamePattern.test(identity) ||
    /^(?:name|full name)$/.test(identity)
    ? { "data-dd-privacy": "mask" }
    : {};
}

const DynamicField: React.FC<DynamicFieldProps> = ({ field, form, styles, setSearchValue }) => {
  const shouldRenderOther = field.type === "radioWithOther" || field.type === "checkboxWithOther";
  const privacyProps = getPrivacyProps(field);

  const renderInputByType = () => {
    switch (field.type) {
      case "text":
        return <Input placeholder={field.placeholder} {...privacyProps} />;

      case "checkbox":
        return <Checkbox {...privacyProps} />;

      case "checkboxWithOther":
        return (
          <Checkbox.Group
            {...privacyProps}
            options={field.options.map((opt: string) => ({
              label: opt,
              value: opt,
            }))}
          />
        );

      case "datepicker":
        return <DatePicker format="DD-MM-YYYY" className="h-[45px] w-full" {...privacyProps} />;

      case "select":
        return (
          <Select
            placeholder="Select"
            className={styles.customSelect}
            showSearch
            optionFilterProp="label"
            filterOption={false}
            {...privacyProps}
            onSearch={(value) => {
              if (field.id === "occupation") {
                setSearchValue(value);
              }
            }}
            disabled={field.disabled}
          >
            {field.options.map((opt: string | { id: number; value: string }, index: number) =>
              typeof opt === "string" ? (
                <Select.Option key={`${opt}-${index}`} value={opt}>
                  {opt}
                </Select.Option>
              ) : (
                <Select.Option key={opt.id ?? `${opt.value}-${index}`} value={opt.id}>
                  {opt.value}
                </Select.Option>
              ),
            )}
          </Select>
        );

      case "radio":
      case "radioWithOther":
        return (
          <Radio.Group className={styles.radioGroup} disabled={field.disabled} {...privacyProps}>
            {field.options.map((opt: string) => (
              <Radio key={opt} value={opt}>
                {opt}
              </Radio>
            ))}
          </Radio.Group>
        );

      case "textarea":
        return (
          <Input.TextArea
            placeholder={field.placeholder}
            rows={2}
            className={styles.textAreaNoResize}
            {...privacyProps}
          />
        );

      default:
        return null;
    }
  };

  if (field.type === "span") {
    return (
      <div className="text-gray-700 mt-1 mb-5" {...privacyProps}>
        {field.value}
      </div>
    );
  }

  return (
    <>
      <Form.Item
        name={field.id}
        className={styles.customForm}
        initialValue={field.type === "checkbox" ? false : undefined}
        valuePropName={field.type === "checkbox" ? "checked" : "value"}
        rules={
          field.type === "checkbox"
            ? []
            : [
                {
                  required: field?.required !== false,
                  message: `${field.label} is required`,
                },
              ]
        }
      >
        {renderInputByType()}
      </Form.Item>

      {shouldRenderOther && (
        <Form.Item
          name={`${field.id}_other`}
          dependencies={[field.id]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selected = getFieldValue(field.id);

                if (
                  (Array.isArray(selected) && selected.includes("Others") && !value) ||
                  (selected === "Others" && !value)
                ) {
                  return Promise.reject(new Error("Please specify other value"));
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input {...privacyProps} />
        </Form.Item>
      )}
    </>
  );
};

export default DynamicField;
